import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import NotesField from "./NotesField";
import { ReasonSelect } from "./ReasonFields";
import { useReasonCodes } from "../../hooks/useReasonCodes";
import { RoleBadge, StandsBadge } from "./LineBadges";
import { usePartyNames } from "../../hooks/usePartyNames";
import { previewSignDown, signDown } from "../../services/PlacingAPI";
import { getApiErrorCode } from "../../utils/apiErrors";
import { showApiErrorToast } from "../../utils/showApiToast";
import { formatDate, readOffer } from "./placingUtils";

const pct = (n) => (n == null ? "—" : `${Math.round(Number(n) * 10000) / 10000}%`);
const diff = (signed, written) => {
  const d = Math.round((Number(signed) - Number(written)) * 10000) / 10000;
  return d === 0 ? "0" : d > 0 ? `+${d}` : `−${Math.abs(d)}`;
};
const time = (v) => new Date(v?.$date ?? v ?? 0).getTime();

const latest = (offers) => [...(offers || [])].sort((a, b) => time(b.stampedDate) - time(a.stampedDate))[0];

/**
 * Where the order stands from the lines view alone (for when the API can't
 * preview): each carrier's latest line, written and standing totals.
 */
const linesTotals = (rows) => {
  const byCarrier = {};
  rows.forEach((r) => (r.offers || []).forEach((o) => (byCarrier[r.partyId] = [...(byCarrier[r.partyId] || []), o])));
  const latestLines = Object.values(byCarrier).map((offers) => readOffer(latest(offers)));
  const round = (n) => Math.round(n * 10000) / 10000;
  return {
    written: round(latestLines.reduce((t, l) => t + (Number(l.writtenShare) || 0), 0)),
    standing: round(latestLines.filter((l) => l.lineToStand).reduce((t, l) => t + (Number(l.writtenShare) || 0), 0)),
  };
};

/**
 * Why there's no usable lead line, from the lead rows of the lines view:
 * [{ partyId, reason }] per lead carrier.
 */
const leadLineProblems = (rows) =>
  rows
    .filter((r) => r.role === "lead")
    .reduce((list, r) => {
      if (list.some((p) => p.partyId === r.partyId)) return list;
      const offers = rows.filter((x) => x.role === "lead" && x.partyId === r.partyId).flatMap((x) => x.offers || []);
      const live = offers.filter((o) => o.status === "sent");
      const unconditional = live.filter((o) => readOffer(o).lineType === "unconditional_line");
      let reason;
      if (offers.length === 0) reason = { kind: "none" };
      else if (live.length === 0) reason = { kind: "closed" };
      else if (unconditional.length === 0) reason = { kind: "conditional", conditions: readOffer(latest(live)).conditions };
      else if (unconditional.every((o) => o.expired)) reason = { kind: "expired" };
      return reason ? [...list, { partyId: r.partyId, ...reason }] : list;
    }, []);

/**
 * Offers the broker may choose for a carrier: sent and not expired;
 * unconditional, unless the broker is accepting conditions.
 */
const signableOffers = (rows, acceptConditions) =>
  rows.flatMap((row) =>
    (row.offers || [])
      .filter((o) => o.status === "sent" && !o.expired && (acceptConditions || readOffer(o).lineType === "unconditional_line"))
      .map((o) => ({ ...o, partyId: row.partyId, role: row.role, offerId: o.offerId || o._id }))
  );

/**
 * The API couldn't preview: say why in terms of the lines on screen (e.g. the
 * lead's only line is conditional) and still show where the order stands.
 */
const PreviewProblem = ({ error, rows, names }) => {
  const totals = linesTotals(rows);
  const leadProblems = getApiErrorCode(error) === "NO_LEAD_LINE" ? leadLineProblems(rows) : [];
  const describe = (p) => {
    const who = names[p.partyId] || "The lead";
    if (p.kind === "conditional") {
      return (
        <>
          <b>{who}</b>'s lead line is conditional{p.conditions ? <> (<i>{p.conditions}</i>)</> : ""}, and conditional lines aren't
          signed unless you accept them. Tick "Accept the conditions" above, or ask {who} to send an unconditional line (it can
          still stand).
        </>
      );
    }
    if (p.kind === "expired") return <><b>{who}</b>'s unconditional lead line has expired. Ask {who} for a new line.</>;
    if (p.kind === "closed") return <><b>{who}</b> has no open lead line. Ask {who} for a new line.</>;
    return <><b>{who}</b> hasn't sent a lead line yet.</>;
  };

  return (
    <VStack align="stretch" spacing={3}>
      <Alert status="warning" fontSize="sm" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <Text>{error.message || "Couldn't preview the signing down."}</Text>
          {leadProblems.map((p) => (
            <Text key={p.partyId} mt={1}>{describe(p)}</Text>
          ))}
          {getApiErrorCode(error) === "NO_LEAD_LINE" && leadProblems.length === 0 && (
            <Text mt={1}>No lead line has been requested or written yet.</Text>
          )}
        </Box>
      </Alert>
      {rows.length > 0 && (
        <SimpleGrid columns={[1, 2]} spacing={4}>
          <Box>
            <Text fontSize="xs" color="gray.500">Written so far (each carrier's latest line, any type)</Text>
            <Text fontSize="xl" fontWeight="semibold">{pct(totals.written)}</Text>
            <Progress value={Math.min(totals.written, 100)} colorScheme={totals.written >= 100 ? "green" : "orange"} size="sm" borderRadius="sm" />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Lines to stand</Text>
            <Text fontSize="xl" fontWeight="semibold">{pct(totals.standing)}</Text>
          </Box>
        </SimpleGrid>
      )}
    </VStack>
  );
};

/**
 * Broker: close the risk to further lines and sign down (once per
 * placement). Previews the signing (changing nothing) whenever the lines or
 * the chosen offers change, then signs exactly the offers the preview used.
 */
const SignDownPanel = ({ placement, lines, comparison, onSigned }) => {
  const toast = useToast();
  const confirm = useDisclosure();
  const [chosen, setChosen] = useState({}); // partyId -> offerId, the broker's changes
  const [leftOut, setLeftOut] = useState([]); // partyIds left out of the signing (NTU'd)
  // Accept the conditions on every conditional line that gets signed (all or nothing;
  // per carrier, pick its unconditional line instead or leave it out)
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [changing, setChanging] = useState(null); // partyId whose offer is being changed
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [ntuReasonCode, setNtuReasonCode] = useState("");
  const [ntuNotes, setNtuNotes] = useState("");
  const { isOther } = useReasonCodes();
  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState(null);

  const rows = useMemo(() => lines?.rows || [], [lines]);
  const options = useMemo(() => signableOffers(rows, acceptConditions), [rows, acceptConditions]);
  const hasConditionalLines = rows.some((r) => (r.offers || []).some((o) => o.status === "sent" && readOffer(o).lineType === "conditional_line"));
  const linesKey = JSON.stringify(rows.map((r) => [r.lineRequestId, r.status, (r.offers || []).map((o) => [o.offerId, o.status])]));
  const chosenKey = JSON.stringify([chosen, leftOut, acceptConditions]);

  // The full list to preview: the preview's own lines with the broker's
  // changes applied and left-out carriers removed; no changes at all means
  // "the API's defaults"
  const offerIdsFor = useCallback(
    (base) => {
      if (Object.keys(chosen).length === 0 && leftOut.length === 0) return undefined;
      const byParty = Object.fromEntries((base?.lines || []).map((l) => [l.partyId, l.offerId]));
      const merged = { ...byParty, ...chosen };
      leftOut.forEach((partyId) => delete merged[partyId]);
      return Object.values(merged);
    },
    // chosenKey stands for chosen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chosenKey]
  );

  const runPreview = useCallback(async () => {
    setLoading(true);
    try {
      setPreview(await previewSignDown(placement._id, offerIdsFor(preview), { acceptConditions }));
      setPreviewError(null);
    } catch (err) {
      setPreviewError(err);
      if (getApiErrorCode(err) === "ALREADY_SIGNED_DOWN") await onSigned();
    } finally {
      setLoading(false);
    }
    // re-run when the lines or the choice change, not on every preview
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement._id, linesKey, chosenKey]);

  useEffect(() => {
    runPreview();
  }, [runPreview]);

  const previewLines = preview?.lines || [];
  const names = usePartyNames([
    ...previewLines.map((l) => l.partyId),
    ...(preview?.excluded || []).map((e) => e.partyId),
    ...rows.map((r) => r.partyId),
  ]);
  const sortedLines = [...previewLines].sort((a, b) => (a.role === "lead" ? 0 : 1) - (b.role === "lead" ? 0 : 1));
  const signedTotal = Math.round(previewLines.reduce((t, l) => t + (Number(l.signedShare) || 0), 0) * 10000) / 10000;
  const writtenTotal = Number(preview?.writtenTotal ?? 0);
  const noScaling = Number(preview?.signingPercentage) === 100;

  const signedOfferIds = previewLines.map((l) => l.offerId);
  // What signing down will NTU, as the preview reports it
  const toNtu = preview?.toNotTakeUp || {};
  const ntuTotal = toNtu.total ?? (toNtu.offers || 0) + (toNtu.lineRequests || 0) + (toNtu.quoteRequests || 0) + (toNtu.quotes || 0);
  const ntuBreakdown = `${toNtu.quoteRequests ?? 0} quote request(s), ${toNtu.quotes ?? 0} quote(s), ${toNtu.lineRequests ?? 0} line request(s) and ${toNtu.offers ?? 0} offer(s)`;
  const ntuReasonNeeded = !!toNtu.reasonRequired;
  const ntuReasonMissing = ntuReasonNeeded && (!ntuReasonCode || (isOther(ntuReasonCode) && !ntuNotes.trim()));

  const doSignDown = async () => {
    setSigning(true);
    try {
      // Exactly what the preview showed (its offers, even if they were the defaults)
      // The same offers and acceptConditions the preview used
      const response = await signDown(placement._id, {
        offerIds: signedOfferIds,
        acceptConditions,
        notes: notes.trim(),
        ...(ntuReasonNeeded && { ntuReasonCode, ntuNotes: ntuNotes.trim() }),
      });
      setResult(response);
      confirm.onClose();
      toast({ title: "Risk closed and signed down", status: "success" });
      await onSigned(response);
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't sign down", duration: 6000 });
      confirm.onClose();
      await runPreview();
    } finally {
      setSigning(false);
    }
  };

  const pick = (partyId, offerId) => {
    setChosen((c) => ({ ...c, [partyId]: offerId }));
    setLeftOut((l) => l.filter((id) => id !== partyId));
    setChanging(null);
  };
  const leaveOut = (partyId) => {
    setLeftOut((l) => [...new Set([...l, partyId])]);
    setChosen((c) => {
      const next = { ...c };
      delete next[partyId];
      return next;
    });
  };
  // Bring a left-out carrier back with its latest usable line
  const includeAgain = (partyId) => {
    const latestOption = options.filter((o) => o.partyId === partyId).sort((a, b) => time(b.stampedDate) - time(a.stampedDate))[0];
    if (latestOption) pick(partyId, latestOption.offerId);
    else setLeftOut((l) => l.filter((id) => id !== partyId));
  };
  const toggleAcceptConditions = (value) => {
    // Back to the API's defaults, which change with the flag
    setChosen({});
    setLeftOut([]);
    setAcceptConditions(value);
  };
  const acceptedLines = previewLines.filter((l) => l.conditionsAccepted);

  if (result) {
    const n = result.notTakenUp || {};
    return (
      <Alert status="success" alignItems="flex-start" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle>Signed down</AlertTitle>
          <AlertDescription fontSize="sm">
            {(result.signedLineAdvices || []).length} signed line advice(s) sent; {result.lineRequestsSigned ?? 0} line request(s)
            signed at {pct(result.signing?.signingPercentage)}. Not taken up: {n.offers ?? 0} offer(s), {n.lineRequests ?? 0} line
            request(s), {n.quoteRequests ?? 0} quote request(s), {n.quotes ?? 0} quote(s).
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
      <HStack justify="space-between" mb={3}>
        <Heading size="sm">Close and sign down</Heading>
        {loading && <Spinner size="sm" />}
      </HStack>

      {hasConditionalLines && (
        <Checkbox mb={3} isChecked={acceptConditions} onChange={(e) => toggleAcceptConditions(e.target.checked)}>
          <Text fontSize="sm">
            Accept the conditions on conditional lines and include them
            <Text as="span" fontSize="xs" color="gray.500">
              {" "}· all or nothing: to keep a carrier's conditions out, choose its unconditional line or leave it out
            </Text>
          </Text>
        </Checkbox>
      )}
      {previewError ? (
        <PreviewProblem error={previewError} rows={rows} names={names} />
      ) : !preview ? (
        <Spinner />
      ) : (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={[1, 3]} spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500">Written total</Text>
              <Text fontSize="xl" fontWeight="semibold">{pct(writtenTotal)}</Text>
              <Progress value={Math.min(writtenTotal, 100)} colorScheme={writtenTotal >= 100 ? "green" : "orange"} size="sm" borderRadius="sm" />
              {writtenTotal < 100 && <Text fontSize="xs" color="orange.600" mt={1}>Written lines total {pct(writtenTotal)}: the order needs 100%</Text>}
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Lines to stand</Text>
              <Text fontSize="xl" fontWeight="semibold">{pct(preview.standingTotal)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Signing down</Text>
              {noScaling ? (
                <Text fontSize="md" fontWeight="semibold">No signing down needed</Text>
              ) : (
                <>
                  <Text fontSize="xl" fontWeight="semibold">to {pct(preview.signingPercentage)}</Text>
                  <Text fontSize="xs" color="gray.600">Lines not standing will be signed at {pct(preview.signingPercentage)} of their written line</Text>
                </>
              )}
            </Box>
          </SimpleGrid>

          <Table size="sm">
            <Thead>
              <Tr><Th>Carrier</Th><Th>Role</Th><Th isNumeric>Written %</Th><Th>Line to stand</Th><Th isNumeric>Signed %</Th><Th isNumeric>Difference</Th><Th /></Tr>
            </Thead>
            <Tbody>
              {sortedLines.map((l) => {
                const carrierOptions = options.filter((o) => o.partyId === l.partyId);
                return (
                  <Tr key={l.offerId}>
                    <Td>
                      <Text fontWeight="semibold">{names[l.partyId] || "Loading…"}</Text>
                      {l.conditional && l.conditions && (
                        <Text fontSize="xs" color="orange.700" whiteSpace="pre-wrap">Conditions: {l.conditions}</Text>
                      )}
                    </Td>
                    <Td><RoleBadge role={l.role} /></Td>
                    <Td isNumeric fontFamily="mono">{pct(l.writtenShare)}</Td>
                    <Td>
                      <HStack spacing={1}>
                        {l.lineToStand && <StandsBadge />}
                        {l.conditionsAccepted && <Badge colorScheme="orange">Accepting conditions</Badge>}
                      </HStack>
                    </Td>
                    <Td isNumeric fontFamily="mono" fontWeight="semibold">{pct(l.signedShare)}</Td>
                    <Td isNumeric fontFamily="mono" color="gray.600">{diff(l.signedShare, l.writtenShare)}</Td>
                    <Td>
                      {changing === l.partyId ? (
                        <Select size="xs" value={l.offerId} onChange={(e) => pick(l.partyId, e.target.value)}>
                          {carrierOptions
                            .sort((a, b) => time(b.stampedDate) - time(a.stampedDate))
                            .map((o) => (
                              <option key={o.offerId} value={o.offerId}>
                                {pct(readOffer(o).writtenShare)} · {o.reference || "no ref"} · {formatDate(o.stampedDate).split(",")[0]}
                              </option>
                            ))}
                        </Select>
                      ) : (
                        <HStack spacing={3}>
                          {carrierOptions.length > 1 && (
                            <Button size="xs" variant="link" colorScheme="blue" onClick={() => setChanging(l.partyId)}>Change</Button>
                          )}
                          {/* A carrier left out is NTU'd at sign-down */}
                          <Button size="xs" variant="link" colorScheme="red" onClick={() => leaveOut(l.partyId)}>Leave out</Button>
                        </HStack>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
            <Tfoot>
              <Tr>
                <Th>Total</Th><Th /><Th isNumeric>{pct(writtenTotal)}</Th><Th isNumeric>{pct(preview.standingTotal)}</Th>
                <Th isNumeric color={signedTotal === 100 ? "green.700" : "red.600"}>{pct(signedTotal)}</Th><Th /><Th />
              </Tr>
            </Tfoot>
          </Table>

          {leftOut.length > 0 && (
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Left out (will be NTU'd)</Text>
              <VStack align="stretch" spacing={1}>
                {leftOut.map((partyId) => (
                  <HStack key={partyId} fontSize="sm" spacing={3}>
                    <Text fontWeight="semibold">{names[partyId] || "Loading…"}</Text>
                    <Button size="xs" variant="link" colorScheme="blue" onClick={() => includeAgain(partyId)}>Include again</Button>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {(preview.excluded || []).length > 0 && (
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Not included</Text>
              <VStack align="stretch" spacing={1}>
                {preview.excluded.map((e) => {
                  const carrierOptions = options.filter((o) => o.partyId === e.partyId);
                  return (
                    <HStack key={`${e.partyId}-${e.offerId}`} fontSize="sm" spacing={3}>
                      <Text fontWeight="semibold">{names[e.partyId] || "Loading…"}</Text>
                      <Text color="gray.600">{e.reason}</Text>
                      {carrierOptions.length > 0 && (
                        <Select size="xs" w="240px" placeholder="Use one of its unconditional lines…" onChange={(ev) => ev.target.value && pick(e.partyId, ev.target.value)}>
                          {carrierOptions.map((o) => (
                            <option key={o.offerId} value={o.offerId}>{pct(readOffer(o).writtenShare)} · {o.reference || "no ref"}</option>
                          ))}
                        </Select>
                      )}
                    </HStack>
                  );
                })}
              </VStack>
            </Box>
          )}

          {!preview.canSignDown && preview.problem && (
            <Alert status="warning" fontSize="sm"><AlertIcon />{preview.problem.message}</Alert>
          )}

          <Box>
            <Text fontSize="xs" color="gray.500">Not taken up when you sign down</Text>
            <Text fontSize="sm">
              {ntuTotal ? `${ntuTotal} outstanding: ${ntuBreakdown}` : "Nothing outstanding to NTU"}
            </Text>
          </Box>
          {ntuReasonNeeded && (
            <SimpleGrid columns={[1, 2]} spacing={3}>
              <ReasonSelect label="Reason for NTU" isRequired value={ntuReasonCode} onChange={setNtuReasonCode} />
              <NotesField
                label={isOther(ntuReasonCode) ? "NTU notes" : "NTU notes (optional)"}
                isRequired={isOther(ntuReasonCode)}
                rows={2}
                value={ntuNotes}
                onChange={setNtuNotes}
              />
            </SimpleGrid>
          )}

          <NotesField label="Notes for the signed line advices" value={notes} onChange={setNotes} />

          <HStack>
            <Button colorScheme="green" size="sm" onClick={confirm.onOpen} isDisabled={!preview.canSignDown || loading || ntuReasonMissing}>
              Close and sign down
            </Button>
            {(Object.keys(chosen).length > 0 || leftOut.length > 0) && (
              <Button size="xs" variant="ghost" onClick={() => { setChosen({}); setLeftOut([]); }}>Back to the default lines</Button>
            )}
          </HStack>
        </VStack>
      )}

      <Modal isOpen={confirm.isOpen} onClose={confirm.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Close and sign down?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2} fontSize="sm">
              <Text>Close this risk to further lines and sign down?</Text>
              <Text>
                • {previewLines.length} line(s) will be signed
                {noScaling ? " (no signing down needed)." : ` (signing down to ${pct(preview?.signingPercentage)}).`}
              </Text>
              <Text>
                • {ntuTotal ? `${ntuTotal} outstanding record(s) will be NTU'd: ${ntuBreakdown}.` : "Nothing outstanding will be NTU'd."}
              </Text>
              {acceptedLines.length > 0 && (
                <Box>
                  <Text>• You'll accept the conditions on {acceptedLines.length} line(s):</Text>
                  {acceptedLines.map((l) => (
                    <Text key={l.offerId} pl={4} color="orange.700" whiteSpace="pre-wrap">
                      {names[l.partyId] || "A carrier"}: {l.conditions || "(no conditions text)"}
                    </Text>
                  ))}
                </Box>
              )}
              <Text>• Signed line advices will be sent to {new Set(previewLines.map((l) => l.partyId)).size} carrier(s).</Text>
              <Text fontWeight="semibold" color="red.600">This can't be undone.</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={confirm.onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={doSignDown} isLoading={signing}>Close and sign down</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SignDownPanel;
